import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { dateDiff, humanizeDuration, keysJoin } from 'fulogics-ui-core';
import withInspector from 'fulogics-ui-web/dist/layout/inspector/hoc/withInspector';
import UserModelSimpleText, {
    UserModelSimpleTextDataKeys,
    UserModelSimpleTextData,
    UserModelSimpleTextDisplayOptions,
} from 'fulogics-ui-web/dist/layout/components/user-model-simple-text/UserModelSimpleText';
import styles from './StateOrderSimpleText.module.scss';
import StateOrderSimpleTextSchema from './StateOrderSimpletTextSchema';
import enOrderState from '../../../api/enums/enOrderState';
import moment from 'moment';
import OrderErrorInfo, {
    OrderErrorInfoData,
    OrderErrorInfoDataKeys,
    OrderErrorInfoDisplayOptions,
} from '../order-error-info/OrderErrorInfo';
import { useUIConfig } from '../../../UIConfig';

function getClassName(state?: enOrderState, asTag?: boolean) {
    return {
        [styles.tag]: asTag,
        [styles.draft]: state === enOrderState.Draft && !asTag,
        [styles.draftTag]: state === enOrderState.Draft && asTag,
        [styles.inProgress]: state === enOrderState.InProgress && !asTag,
        [styles.inProgressTag]: state === enOrderState.InProgress && asTag,
        [styles.completed]: state === enOrderState.Completed && !asTag,
        [styles.completedTag]: state === enOrderState.Completed && asTag,
        [styles.none]: state === enOrderState.CancelledByTMS && !asTag,
        [styles.noneTag]: state === enOrderState.CancelledByTMS && asTag,
        [styles.error]: state === enOrderState.Error && !asTag,
        [styles.errorTag]: state === enOrderState.Error && asTag,
        [styles.processing]:
            (state === enOrderState.SentToWMSAndTMS ||
                state === enOrderState.SendingInProgress ||
                state === enOrderState.SentToWMS) &&
            !asTag,
        [styles.processingTag]:
            (state === enOrderState.SentToWMSAndTMS ||
                state === enOrderState.SendingInProgress ||
                state === enOrderState.SentToWMS) &&
            asTag,
    };
}

const StateOrderSimpleText = memo((props: StateOrderSimpleTextProps) => {
    const { t } = useTranslation(['StateOrderSimpleText', 'orderStatus']);
    const {
        StateOrderSimpleText: { deadlineVisibleMs },
    } = useUIConfig();
    const state = props.data?.state;
    const isEdited = state === enOrderState.Draft && props?.data?.currentEditing?.editingBy;

    const deadline = moment(props?.data?.currentEditing?.editingDeadline || new Date());

    const deadlineMs = dateDiff(deadline.toDate(), new Date());
    const deadlineVisible = deadlineMs > 0 && deadlineMs <= deadlineVisibleMs;

    return (
        <div
            className={classNames(props.className, {
                [styles.errorWrapper]: props.showErrorInfo,
            })}
        >
            <div
                className={classNames(styles.container, {
                    [styles.column]: !props?.inline,
                    [styles.deadlineGrid]: props?.showDeadlineInfo,
                })}
            >
                <label className={classNames(getClassName(state, props.statusAsTag))}>
                    {isEdited ? t('orderStatus:edited') : t(`orderStatus:${state}`)}
                </label>
                {isEdited && (
                    <UserModelSimpleText
                        data={props.data?.currentEditing?.editingBy}
                        className={classNames({
                            [styles.userWithMargin]: !props?.showDeadlineInfo && props?.inline,
                        })}
                        {...props.userModelSimpleTextDisplayOptions}
                    />
                )}
                {props?.showDeadlineInfo && deadlineVisible && (
                    <>
                        <label className={classNames(styles.deadlineInfo)}>{t('blockedBy')}</label>
                        <label className={classNames(styles.deadlineInfo)}>
                            {humanizeDuration(deadlineMs)}
                        </label>
                    </>
                )}
            </div>
            {props.showErrorInfo && (
                <OrderErrorInfo
                    data={props.data}
                    {...props.orderErrorInfoDisplayOptions}
                    className={classNames(styles.errorInfo)}
                />
            )}
        </div>
    );
});

export type StateOrderSimpleTextProps = {
    data?: StateOrderSimpleTextData;
} & StateOrderSimpleTextDisplayOptions;

export default withInspector<StateOrderSimpleTextProps>(
    StateOrderSimpleText,
    StateOrderSimpleTextSchema
);

export type StateOrderSimpleTextDisplayOptions = {
    id?: string;
    className?: string;
    inline?: boolean;
    statusAsTag?: boolean;
    showDeadlineInfo?: boolean;
    showErrorInfo?: boolean;
    //
    userModelSimpleTextDisplayOptions?: UserModelSimpleTextDisplayOptions;
    orderErrorInfoDisplayOptions?: OrderErrorInfoDisplayOptions;
};

export type StateOrderSimpleTextData = {
    currentEditing?: {
        editingBy?: UserModelSimpleTextData;
        editingDeadline?: Date;
    };
} & OrderErrorInfoData;

export const StateOrderSimpleTextDataKeys = [
    keysJoin(UserModelSimpleTextDataKeys, 'currentEditing.editingBy'),
    'currentEditing.editingDeadline',
    ...OrderErrorInfoDataKeys,
];
